/**
 * MyCopro - Types Résidence
 * Avec double identifiant : technique (Firestore) et client-facing (RES-xxxxx-XXX)
 * Inclut la gestion du mode bootstrap pour l'onboarding
 * 
 * ✅ CORRIGÉ:
 * - SubscriptionStatus inclut maintenant 'trial' et 'grace'
 * - Interface Subscription alignée avec Cloud Functions (graceStartDate, lastReminderSent, etc.)
 * - createDefaultResidence() crée un abonnement en mode 'trial' (30 jours) au lieu de 'active' (1 an)
 * - calculateSubscriptionInfo() supporte les statuts trial et grace
 */

// ============================================================================
// CONTACTS (Syndic, Gardien)
// ============================================================================

export interface SyndicContact {
  name: string;
  phone: string;
  email: string;
  hours: string;
  portalUrl?: string;  // ✅ NOUVEAU: URL du portail copropriétaire du syndic
}

export interface GardienContact {
  name: string;
  phone: string;
  hours: string;
}

// ============================================================================
// CODES D'ACCÈS
// ============================================================================

export interface AccessCodes {
  portail: string;
  hall: string;
  parking: string;
}

// ============================================================================
// COLLECTE DES DÉCHETS
// ============================================================================

export interface TrashCollection {
  om: string;
  recyclage: string;
  verre: string;
  encombrants?: string;
}

// ============================================================================
// ASCENSEUR
// ============================================================================

export interface ElevatorBuildingConfig {
  device1?: string;  // Numéro appareil ascenseur 1 (unique, ou "Droit" si deux)
  device2?: string;  // Numéro appareil ascenseur 2 (optionnel, "Gauche" si deux)
}

export interface ElevatorInfo {
  company: string;
  phone: string;
  deviceNumber?: string;                                    // Résidence mono-bâtiment
  buildingDevices?: Record<string, ElevatorBuildingConfig>; // Multi-bâtiments : par bâtiment
}

// ============================================================================
// ACCÈS LOCAUX
// ============================================================================

export interface AccessLocations {
  fibre: string;
  parking: string;
  velos: string;
}

// ============================================================================
// STATISTIQUES RÉSIDENCE
// ============================================================================

export interface ResidenceStats {
  apartments: number;
  owners: number;
  tenants: number;
}

// ============================================================================
// ABONNEMENT
// ✅ CORRIGÉ: Types alignés avec Cloud Functions (subscriptions.ts)
// ============================================================================

export type SubscriptionStatus = 'active' | 'expiring' | 'expired' | 'trial' | 'grace';

export interface Subscription {
  status: SubscriptionStatus;
  startDate?: string;           // Date de début (ISO)
  endDate: string;              // Date de fin (ISO)
  plan?: 'small' | 'medium' | 'large' | 'xlarge' | null;  // Plan tarifaire
  autoRenew?: boolean;          // Renouvellement automatique
  statusUpdatedAt?: string;     // Dernière mise à jour du statut (ISO)
  lastReminderSent?: string;    // Dernier rappel envoyé (YYYY-MM-DD)
  graceStartDate?: string;      // Date début période de grâce (ISO)
  stripeSubscriptionId?: string; // ID subscription Stripe (sub_xxx)
  stripeCustomerId?: string;     // ID customer Stripe (cus_xxx) — un par résidence, déterministe
}

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  daysRemaining: number;
  endDate: string;
  isExpired: boolean;
  isExpiring: boolean;
  isTrial: boolean;
  isGrace: boolean;
  graceDaysRemaining: number | null;
}

// ============================================================================
// BAILLEUR SOCIAL
// ============================================================================

export type BailleurType = 'social' | 'private';

export interface Bailleur {
  id: string;
  name: string;
  type?: BailleurType;
  contact?: string;
  phone?: string;
  email?: string;
  apartments?: string[];
}

// ============================================================================
// MODE BOOTSTRAP (onboarding nouvelle résidence)
// ============================================================================

export type SetupMode = 'bootstrap' | 'active';

// ============================================================================
// RÉSIDENCE COMPLÈTE
// ============================================================================

export interface Residence {
  id: string;                    // ID technique Firestore (interne)
  residenceCode: string;         // ID client-facing MyCopro (ex: RES-75015-A3K)
  syndicId?: string;             // Référence auprès du syndic
  name: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  photo?: string;
  // Bâtiments — null/[] = résidence à bâtiment unique (cas classique)
  buildings?: string[] | null;
  // Codes d'accès spécifiques par bâtiment (viennent en complément des codes communs)
  buildingCodes?: Record<string, Partial<AccessCodes>> | null;
  hasGardien: boolean;
  syndic: SyndicContact;
  gardien?: GardienContact;
  codes: AccessCodes;
  trash: TrashCollection;
  elevator?: ElevatorInfo;
  access?: AccessLocations;
  stats: ResidenceStats;
  subscription?: Subscription;
  bailleurs: Bailleur[];
  hasSocialBailleur?: boolean | null;

  /**
   * Résidence pré-créée par un admin (avec photo/infos) avant qu'un vrai
   * utilisateur ne la rejoigne — l'essai de 30 jours n'est volontairement
   * pas démarré (subscription absent) tant que preSeeded est true.
   * Basculé à false par la Cloud Function d'activation de l'essai.
   */
  preSeeded?: boolean;
  
  // ============================================================
  // MODE BOOTSTRAP - Gestion de l'onboarding nouvelle résidence
  // ============================================================
  
  /**
   * Mode de la résidence : 'bootstrap' ou 'active'
   * - 'bootstrap': Pas encore de CS, permissions limitées
   * - 'active': Au moins un CS actif, fonctionnement normal
   */
  setupMode?: SetupMode;
  
  /**
   * Limite de membres auto-validés en mode bootstrap (défaut: 10)
   * Modifiable uniquement par admin dans Firestore
   */
  bootstrapLimit?: number;
  
  /**
   * Nombre de membres actifs dans la résidence
   */
  memberCount?: number;
  
  /**
   * Indique si un CS actif existe dans la résidence
   */
  hasActiveCouncil?: boolean;
  
  /**
   * Date de fin du mode bootstrap
   */
  bootstrapEndedAt?: string;
  
  /**
   * ID du premier membre du CS
   */
  firstCouncilMemberId?: string;
  
  // ============================================================
  // Conformité plan — géré par checkPlanCompliance CF
  // ============================================================
  planAlert?: {
    activeUsers: number;
    plan: 'small' | 'medium' | 'large';
    detectedAt: string;
    lastNotifiedAt: string;
  } | null;
  activeUsersCount?: number;

  // ============================================================
  // Paramètres chat
  // ============================================================
  chatSettings?: {
    residence: boolean;
    owners: boolean;
    council: boolean;
  };

  // ============================================================
  // Métadonnées
  // ============================================================
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

// ============================================================================
// CRÉATION DE RÉSIDENCE
// ============================================================================

export interface CreateResidenceData {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  country?: string;
  createdBy: string;
  buildings?: string[] | null;
  /**
   * Si true : résidence pré-créée par un admin, subscription non initialisée.
   * L'essai de 30 jours démarrera au premier vrai membre (CS ou 5e membre).
   */
  preSeeded?: boolean;
}

// ============================================================================
// MISE À JOUR RÉSIDENCE
// ============================================================================

export interface UpdateResidenceData {
  syndicId?: string;
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  photo?: string | null;
  hasGardien?: boolean;
  syndic?: Partial<SyndicContact>;
  gardien?: Partial<GardienContact> | null;
  codes?: Partial<AccessCodes>;
  trash?: Partial<TrashCollection>;
  elevator?: Partial<ElevatorInfo> | null;
  access?: Partial<AccessLocations>;
  stats?: Partial<ResidenceStats>;
}

// ============================================================================
// RECHERCHE RÉSIDENCE
// ============================================================================

export interface ResidenceSearchResult {
  id: string;
  residenceCode: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  photo?: string;
  membersCount: number;
  // Mode bootstrap
  hasActiveCouncil?: boolean;
  setupMode?: SetupMode;
  memberCount?: number;
  bootstrapLimit?: number;
}

// ============================================================================
// GÉNÉRATION DU CODE RÉSIDENCE CLIENT-FACING
// ============================================================================

function generateRandomCode(length: number = 3): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function generateResidenceCode(postalCode: string, firestoreId: string): string {
  const cleanPostalCode = (postalCode || '00000')
    .replace(/\D/g, '')
    .slice(0, 5)
    .padStart(5, '0');
  
  const idSuffix = (firestoreId || '')
    .slice(-3)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  
  const uniqueCode = idSuffix.length === 3 ? idSuffix : generateRandomCode(3);
  
  return `RES-${cleanPostalCode}-${uniqueCode}`;
}

export function isValidResidenceCode(code: string): boolean {
  const pattern = /^RES-\d{5}-[A-Z0-9]{3}$/;
  return pattern.test(code);
}

export function extractPostalCodeFromResidenceCode(residenceCode: string): string | null {
  if (!isValidResidenceCode(residenceCode)) return null;
  return residenceCode.split('-')[1];
}

// ============================================================================
// HELPERS
// ============================================================================

const GRACE_PERIOD_DAYS = 7;

/**
 * Calcule les informations détaillées de l'abonnement
 * ✅ Supporte maintenant trial et grace
 */
export function calculateSubscriptionInfo(subscription: Subscription): SubscriptionInfo {
  const today = new Date();
  const endDate = new Date(subscription.endDate);
  const diffTime = endDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Utiliser le statut Firestore comme source de vérité
  let status: SubscriptionStatus = subscription.status;
  let graceDaysRemaining: number | null = null;
  
  // Calculer les jours restants en période de grâce
  if (status === 'grace' && subscription.graceStartDate) {
    const graceStart = new Date(subscription.graceStartDate);
    const daysSinceGrace = Math.floor((today.getTime() - graceStart.getTime()) / (1000 * 60 * 60 * 24));
    graceDaysRemaining = Math.max(0, GRACE_PERIOD_DAYS - daysSinceGrace);
  }
  
  // Recalculer le statut si nécessaire (pour les cas où le cron n'a pas encore tourné)
  if (status === 'active' && daysRemaining <= 0) {
    status = 'grace'; // Devrait passer en grâce
  } else if (status === 'active' && daysRemaining <= 30) {
    status = 'expiring';
  } else if (status === 'trial' && daysRemaining <= 0) {
    status = 'grace'; // Trial expiré → grâce
  }
  
  return {
    status,
    daysRemaining,
    endDate: subscription.endDate,
    isExpired: status === 'expired',
    isExpiring: status === 'expiring',
    isTrial: status === 'trial',
    isGrace: status === 'grace',
    graceDaysRemaining
  };
}

/**
 * ✅ CORRIGÉ: Crée une résidence par défaut en mode TRIAL (30 jours)
 * au lieu de 'active' (1 an)
 */
export function createDefaultResidence(data: CreateResidenceData): Omit<Residence, 'id' | 'residenceCode'> {
  const now = new Date();
  const nowISO = now.toISOString();

  const base: Omit<Residence, 'id' | 'residenceCode' | 'subscription'> = {
    syndicId: '',
    name: data.name,
    address: data.address,
    postalCode: data.postalCode,
    city: data.city,
    country: data.country || 'France',
    photo: undefined,
    hasGardien: false,
    syndic: { name: '', phone: '', email: '', hours: '' },
    gardien: undefined,
    codes: { portail: '', hall: '', parking: '' },
    trash: { om: '', recyclage: '', verre: '' },
    elevator: undefined,
    access: { fibre: '', parking: '', velos: '' },
    stats: { apartments: 0, owners: 0, tenants: 0 },
    bailleurs: [],

    // Mode bootstrap par défaut pour les nouvelles résidences
    setupMode: 'bootstrap',
    bootstrapLimit: 10,
    memberCount: 0,
    hasActiveCouncil: false,
    bootstrapEndedAt: undefined,
    firstCouncilMemberId: undefined,

    preSeeded: !!data.preSeeded,

    buildings: data.buildings && data.buildings.length > 1 ? data.buildings : null,
    chatSettings: { residence: true, owners: true, council: true },
    createdAt: nowISO,
    createdBy: data.createdBy,
    updatedAt: nowISO
  };

  // ✅ Résidence pré-créée par un admin : pas de subscription tant qu'aucun
  // vrai membre n'a rejoint — voir activateResidenceTrial (Cloud Function)
  if (data.preSeeded) {
    return base;
  }

  // ✅ Trial de 30 jours au lieu de 1 an en actif
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 30);

  return {
    ...base,
    // ✅ Abonnement en mode trial (30 jours)
    subscription: {
      status: 'trial',
      startDate: nowISO,
      endDate: trialEndDate.toISOString(),
      plan: null,
      autoRenew: false
    }
  };
}

// ============================================================================
// HELPERS MODE BOOTSTRAP
// ============================================================================

/**
 * Vérifie si la résidence est en mode bootstrap
 */
export function isInBootstrapMode(residence: Residence | null): boolean {
  if (!residence) return false;
  return (residence.setupMode === 'bootstrap' || !residence.setupMode) && !residence.hasActiveCouncil;
}

/**
 * Vérifie si un nouveau membre doit être auto-validé
 * Conditions: mode bootstrap ET (pas de CS OU moins de bootstrapLimit membres)
 */
export function shouldAutoValidateMember(residence: Residence | null): boolean {
  if (!residence) return false;
  
  // Si mode actif (CS existe), pas d'auto-validation
  if (residence.setupMode === 'active' || residence.hasActiveCouncil) {
    return false;
  }
  
  // En mode bootstrap, auto-valider si moins de bootstrapLimit membres
  const limit = residence.bootstrapLimit || 10;
  const count = residence.memberCount || 0;
  return count < limit;
}

/**
 * Vérifie si la catégorie Conseil Syndical est disponible pour l'inscription
 * Disponible uniquement s'il n'y a pas encore de CS actif
 */
export function isCouncilCategoryAvailable(residence: Residence | null): boolean {
  if (!residence) return true; // Nouvelle résidence = CS disponible
  return !residence.hasActiveCouncil;
}
